import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip, Popover, Table, Tag } from 'antd';
import { DatabaseOutlined, ShopOutlined } from '@ant-design/icons';
import api from '../../services/api';
import EFluidType from '../../enums/EFluidType';

/**
 * HeaderStockTotals - estoque total da empresa em evidência no header do painel admin.
 * Mostra dois chips em destaque: "Comprado" (bruto) e "Disponível" (descarregado - vendido),
 * além de chips por componente (Solvente, Borra, Água, Solvente Amarelo) com o valor disponível.
 * Um Popover traz o detalhamento completo (comprado, disponível, descarregado, vendido) por componente.
 *
 * Em modo compact (mobile) mostra apenas os dois chips de total + o Popover, para economizar espaço.
 *
 * Eficiência: 1 endpoint (GET /api/dashboard/stock-summary), polling de 60s + refetch
 * quando a aba volta ao foco (visibilitychange). Falhas são silenciosas no header.
 */
const POLL_INTERVAL = 60_000;

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

const COMPONENTS = [
  EFluidType.SOLVENT.code,
  EFluidType.BORRA.code,
  EFluidType.WATER.code,
  EFluidType.YELLOW_SOLVENT.code
];

const SHORT_LABEL = {
  [EFluidType.SOLVENT.code]: 'Solv',
  [EFluidType.BORRA.code]: 'Borra',
  [EFluidType.WATER.code]: 'Água',
  [EFluidType.YELLOW_SOLVENT.code]: 'Amarelo'
};

const HeaderStockTotals = ({ compact }) => {
  const [data, setData] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.dashboard.getStockSummary();
      setData(res.data || null);
    } catch (err) {
      // Silencioso no header: não incomoda o usuário com toast a cada minuto.
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar estoque total:', err?.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const interval = setInterval(fetchSummary, POLL_INTERVAL);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchSummary();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchSummary]);

  const components = data?.components || {};
  const total = Number(data?.totals?.disponivel || 0);
  const totalBruto = Number(data?.totals?.bruto || 0);
  // Transparência da conferência: quanto do comprado é real (conferido) vs suposto (200L/tambor pendente).
  const conferidoLitros = Number(data?.conferencia?.conferredLiters || 0);
  const pendenteLitros = Number(data?.conferencia?.pendingLiters || 0);
  const pendenteTambores = Number(data?.conferencia?.pendingDrums || 0);

  const chipStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '1px 6px',
    borderRadius: 12,
    background: '#f5f7fa',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: '#1b3663'
  };

  const totalChipStyle = {
    ...chipStyle,
    color: '#fff',
    fontSize: 12,
    padding: '1px 8px'
  };

  const detailColumns = [
    { title: 'Componente', dataIndex: 'label', key: 'label' },
    { title: 'Comprado (L)', dataIndex: 'bruto', key: 'bruto', align: 'right', render: fmt },
    { title: 'Disponível (L)', dataIndex: 'disponivel', key: 'disponivel', align: 'right', render: fmt },
    { title: 'Descarregado (L)', dataIndex: 'drenado', key: 'drenado', align: 'right', render: fmt },
    { title: 'Vendido (L)', dataIndex: 'retirado', key: 'retirado', align: 'right', render: fmt }
  ];

  const detailData = COMPONENTS.map((c) => ({
    key: c,
    label: EFluidType.getByCode(c)?.description || c,
    bruto: Number(components[c]?.bruto || 0),
    disponivel: Number(components[c]?.disponivel || 0),
    drenado: Number(components[c]?.drenado || 0),
    retirado: Number(components[c]?.retirado || 0)
  }));

  const popoverContent = (
    <div style={{ minWidth: 420 }}>
      <Table
        size="small"
        pagination={false}
        columns={detailColumns}
        dataSource={detailData}
        locale={{ emptyText: 'Sem dados de estoque' }}
        summary={(rows) => {
          const t = rows.reduce((acc, r) => ({
            bruto: acc.bruto + (r.bruto || 0),
            disponivel: acc.disponivel + (r.disponivel || 0),
            drenado: acc.drenado + (r.drenado || 0),
            retirado: acc.retirado + (r.retirado || 0)
          }), { bruto: 0, disponivel: 0, drenado: 0, retirado: 0 });
          return (
            <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
              <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">{fmt(t.bruto)}</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">{fmt(t.disponivel)}</Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">{fmt(t.drenado)}</Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">{fmt(t.retirado)}</Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
      {/* Totalizadores da conferência: deixa claro quanto do comprado é real vs suposto. */}
      <div
        style={{ marginTop: 8, fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}
        aria-label={`Conferido: ${fmt(conferidoLitros)} litros. Não conferido (suposto): ${fmt(pendenteLitros)} litros em ${pendenteTambores} tambores.`}
      >
        Conferido: <strong style={{ color: '#2f7d32' }}>{fmt(conferidoLitros)} L</strong>
        {' · '}
        Não conferido (suposto): <strong style={{ color: '#b45309' }}>{fmt(pendenteLitros)} L</strong>
        {pendenteTambores > 0 && (
          <span> ({pendenteTambores} {pendenteTambores === 1 ? 'tambor' : 'tambores'} × 200 L)</span>
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      role="status"
      aria-label={`Estoque comprado: ${fmt(totalBruto)} litros, disponível: ${fmt(total)} litros`}
    >
      <Tooltip
        title={
          <span>
            Total comprado/conferido (inclui não processado)
            <br />Conferido: {fmt(conferidoLitros)} L
            <br />Não conferido (suposto): {fmt(pendenteLitros)} L
            {pendenteTambores > 0 && ` (${pendenteTambores} ${pendenteTambores === 1 ? 'tambor' : 'tambores'} × 200 L)`}
          </span>
        }
      >
        <Tag style={{ ...totalChipStyle, background: '#0d7dd6', cursor: 'default' }} color="blue">
          <ShopOutlined />
          <span>{compact ? 'Comp:' : 'Comprado:'}</span>
          <span>{fmt(totalBruto)} L</span>
        </Tag>
      </Tooltip>

      <Popover content={popoverContent} title="Estoque por componente" trigger="click" placement="bottomRight">
        <Tag style={{ ...totalChipStyle, background: '#1b3663', cursor: 'pointer' }} color="blue">
          <DatabaseOutlined />
          <span>{compact ? 'Disp:' : 'Disponível:'}</span>
          <span>{fmt(total)} L</span>
        </Tag>
      </Popover>

      {!compact && COMPONENTS.map((c) => (
        <Tooltip key={c} title={`${EFluidType.getByCode(c)?.description || c} — comprado ${fmt(components[c]?.bruto || 0)} L / disponível ${fmt(components[c]?.disponivel || 0)} L`}>
          <Tag style={chipStyle} color="default">
            <span>{SHORT_LABEL[c] || c}</span>
            <span>{fmt(components[c]?.disponivel || 0)} L</span>
          </Tag>
        </Tooltip>
      ))}
    </div>
  );
};

export default HeaderStockTotals;
